/* ============================================================
   HYRALUXE — global chrome behaviour.

   Ported from the prototype's js/app.js. Everything that was driven by the
   prototype's in-memory catalogue is now rendered by Liquid; what is left here
   is the behaviour: drawers, focus handling, the toast, the announcement,
   the wishlist, and the cart — which now talks to Shopify's Cart AJAX API and
   redraws itself from the Section Rendering API rather than from a JS copy of
   the cart, because a hand-kept copy is exactly what drifts.
   ============================================================ */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const H = window.HYRA || {};
  const routes = H.routes || {};

  /* ---------------- Money ----------------
     JS-built prices have to read exactly like Liquid-built ones. Formatting
     with a hardcoded symbol meant a price changed shape the moment a swatch was
     clicked, so this renders through the shop's own money_format instead. */
  const money = cents => {
    const fmt = H.moneyFormat || '₹{{amount}}';
    const value = (cents || 0) / 100;

    const group = (n, decimals, sep) => {
      const fixed = Math.abs(n).toFixed(decimals);
      const parts = fixed.split('.');
      /* Indian grouping: last three digits, then pairs. */
      let int = parts[0];
      let head = int.length > 3 ? int.slice(0, -3) : '';
      const tail = int.length > 3 ? int.slice(-3) : int;
      if (head) head = head.replace(/(\d)(?=(\d\d)+$)/g, '$1' + sep);
      let out = head ? head + sep + tail : tail;
      if (decimals > 0) out += '.' + parts[1];
      return out;
    };

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount': return group(value, 2, ',');
        case 'amount_no_decimals': return group(value, 0, ',');
        case 'amount_with_comma_separator': return group(value, 2, '.');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'");
        default: return group(value, 2, ',');
      }
    });
  };
  window.hyraMoney = money;

  /* Shopify hands prices back in two shapes: /cart.js and /products/x.js give
     an integer number of paise, while predictive search gives a string in
     rupees ("899.00"). Treating one as the other turns Rs 899 into Rs 8.99. */
  const toCents = v => {
    if (typeof v === 'number') return v;
    const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  };

  /* ---------------- Toast ---------------- */
  let toastTimer;
  const toast = msg => {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
  };
  window.hyraToast = toast;

  /* ---------------- Image retry ----------------
     A single dropped image request should not leave a hole in the grid. */
  document.addEventListener('error', e => {
    const img = e.target;
    if (img && img.tagName === 'IMG' && !img.dataset.retried && img.currentSrc) {
      img.dataset.retried = '1';
      const src = img.currentSrc;
      setTimeout(() => { img.removeAttribute('src'); img.src = src; }, 1200);
    }
  }, true);

  /* ---------------- Drawers, sheets, focus ---------------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let lastFocus = null;

  const overlay = () => $('#overlay');

  const openEl = el => {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lock');
    if (el.classList.contains('drawer')) document.body.classList.add('drawer-open');
    if (overlay()) overlay().classList.add('show');
    const first = el.querySelector(FOCUSABLE);
    if (first) setTimeout(() => first.focus(), 30);
  };
  const closeAll = () => {
    const wasOpen = $$('.drawer.open, .sheet.open');
    wasOpen.forEach(el => { el.classList.remove('open'); el.setAttribute('aria-hidden', 'true'); });
    if (overlay()) overlay().classList.remove('show');
    document.body.classList.remove('lock');
    document.body.classList.remove('drawer-open');
    if (wasOpen.length && lastFocus && document.contains(lastFocus)) { lastFocus.focus(); lastFocus = null; }
  };
  window.hyraOpen = openEl;
  window.hyraCloseAll = closeAll;

  document.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    const zoom = $('.zoom'), modal = $('.modal'), viewer = $('#reel-viewer');
    const open = $('.drawer.open, .sheet.open')
      || (viewer && !viewer.hidden && viewer)
      || (zoom && !zoom.hidden && zoom)
      || (modal && !modal.hidden && modal);
    if (!open) return;
    const items = [...open.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeAll(); });

  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-open]');
    if (opener) { const el = document.getElementById(opener.dataset.open); if (el) { closeAll(); openEl(el); } return; }
    if (e.target.closest('[data-close]') || e.target.closest('#overlay')) { closeAll(); return; }
    if (e.target.closest('[data-history-back]')) {
      e.preventDefault();
      if (history.length > 1) history.back(); else location.href = routes.root || '/';
      return;
    }
    const t = e.target.closest('[data-toast]');
    if (t) { e.preventDefault(); toast(t.dataset.toast); }
  });

  /* ---------------- Announcement rotation ----------------
     One message at a time, fading. Held still for reduced motion. */
  const ann = $('#announce');
  if (ann && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let list = [];
    try { list = JSON.parse(ann.dataset.messages || '[]'); } catch (err) { list = []; }
    if (list.length > 1) {
      let i = 0;
      const gap = +ann.dataset.interval || 4200;
      ann.style.transition = 'opacity .3s';
      setInterval(() => {
        ann.style.opacity = '0';
        setTimeout(() => {
          i = (i + 1) % list.length;
          ann.textContent = list[i];
          ann.style.opacity = '1';
        }, 300);
      }, gap);
    }
  }

  /* ---------------- Footer accordions ---------------- */
  document.addEventListener('click', e => {
    const head = e.target.closest('.foot-acc__head');
    if (!head) return;
    const box = head.parentElement;
    box.classList.toggle('open');
    head.setAttribute('aria-expanded', String(box.classList.contains('open')));
  });

  /* ---------------- Payment strip: reveal net banking / EMI ---------------- */
  const payToggle = $('#pay-toggle');
  if (payToggle) payToggle.addEventListener('click', () => {
    const more = $('#pay-more');
    const open = more.hidden;
    more.hidden = !open;
    payToggle.setAttribute('aria-expanded', String(open));
    payToggle.classList.toggle('open', open);
    $('#pay-toggle-label').textContent = open ? 'FEWER PAYMENT OPTIONS' : 'MORE PAYMENT OPTIONS';
  });

  /* ============================================================
     CART — Shopify Cart AJAX API + Section Rendering API
     ============================================================ */
  const setBadge = n => {
    $$('[data-cart-badge]').forEach(b => { b.textContent = n; b.hidden = n === 0; });
  };

  /* Redraw the drawer (and the cart page, when we are on it) from the server's
     own render, so the markup can never disagree with the cart. */
  const refreshCart = async (opts) => {
    const open = opts && opts.open;
    const wasOpen = !!$('#cart-drawer.open');
    /* A section that lives in a JSON template is wrapped as
       `shopify-section-template--123__cart-page`, not `shopify-section-cart-page`
       — so the id has to be read off the page rather than assumed, or the bag
       silently never redraws and drifts from the real cart. */
    const wanted = ['cart-drawer'];
    const pageHost = document.querySelector('[id^="shopify-section-"][id$="cart-page"]');
    if (pageHost) wanted.push(pageHost.id.replace('shopify-section-', ''));

    const res = await fetch((routes.root || '/') + '?sections=' + wanted.join(','));
    const data = await res.json();

    wanted.forEach(name => {
      const html = data[name];
      const host = document.getElementById('shopify-section-' + name);
      if (!html || !host) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById('shopify-section-' + name);
      if (fresh) host.replaceWith(fresh);
    });

    const state = await (await fetch((routes.cart || '/cart') + '.js')).json();
    setBadge(state.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));

    if (open || wasOpen) openEl($('#cart-drawer'));
    paintWishHearts();
    return state;
  };

  const addToBag = async (variantId, qty, properties) => {
    qty = qty || 1;
    const item = { id: Number(variantId), quantity: qty };
    if (properties) item.properties = properties;
    const res = await fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast(err.description || err.message || 'Could not add to bag.');
      return null;
    }
    await refreshCart({ open: true });
    toast(qty > 1 ? qty + ' added to your bag.' : 'Added to your bag.');
    return true;
  };
  window.hyraAddToBag = addToBag;

  const changeLine = async (key, quantity) => {
    const res = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
    if (!res.ok) { toast('Could not update your bag.'); return; }
    await refreshCart();
  };

  /* Quantity steppers and remove, in the drawer and on the cart page. */
  document.addEventListener('click', e => {
    const item = e.target.closest('[data-key]');
    if (!item) return;
    const key = item.dataset.key;

    const qbtn = e.target.closest('[data-qty]');
    if (qbtn) {
      e.preventDefault();
      const cell = item.querySelector('.qty span');
      const cur = +((cell && cell.textContent) || 1);
      const next = cur + (+qbtn.dataset.qty);
      changeLine(key, Math.max(next, 0));
      return;
    }
    if (e.target.closest('[data-remove]')) {
      e.preventDefault();
      changeLine(key, 0).then(() => toast('Removed from bag'));
    }
  });

  /* Other scripts (the wishlist page's MOVE ALL) hand the cart back to us
     rather than keeping their own copy of the drawer markup. */
  document.addEventListener('cart:needs-refresh', () => { refreshCart({ open: true }); });

  /* Add-to-bag buttons anywhere in the theme. */
  document.addEventListener('click', e => {
    const ab = e.target.closest('[data-add]');
    if (!ab || ab.disabled) return;
    e.preventDefault();
    const qtyField = ab.dataset.qtyFrom ? $(ab.dataset.qtyFrom) : null;
    const qty = +(ab.dataset.addqty || (qtyField && qtyField.textContent) || 1) || 1;
    ab.classList.add('is-busy');
    addToBag(ab.dataset.add, qty).then(() => ab.classList.remove('is-busy'),
                                       () => ab.classList.remove('is-busy'));
  });

  /* ============================================================
     WISHLIST — localStorage, keyed by product handle
     ============================================================ */
  const WKEY = 'hyraluxe:wishlist';
  const wishGet = () => {
    try { return JSON.parse(localStorage.getItem(WKEY)) || []; } catch (err) { return []; }
  };
  const wishSet = list => localStorage.setItem(WKEY, JSON.stringify(list));
  window.hyraWishlist = wishGet;

  function paintWishHearts() {
    const list = wishGet();
    $$('[data-wish]').forEach(b => b.classList.toggle('active', list.includes(b.dataset.wish)));
    $$('[data-wish-count]').forEach(el => { el.textContent = list.length; });
  }
  window.hyraPaintHearts = paintWishHearts;

  const toggleWish = handle => {
    const list = wishGet();
    const i = list.indexOf(handle);
    if (i > -1) { list.splice(i, 1); toast('Removed from wishlist'); }
    else { list.push(handle); toast('Saved to wishlist'); }
    wishSet(list);
    paintWishHearts();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: list }));
  };

  document.addEventListener('click', e => {
    const w = e.target.closest('[data-wish]');
    if (!w) return;
    e.preventDefault();
    toggleWish(w.dataset.wish);
  });

  /* "Save for later" on a bag line: park it in the wishlist and take it out of
     the cart. Written out rather than reusing toggleWish so it can never
     un-save something already on the list, and so it toasts once, not twice. */
  document.addEventListener('click', e => {
    const s = e.target.closest('[data-save-later]');
    if (!s) return;
    e.preventDefault();
    const row = s.closest('[data-key]');
    if (!row) return;
    const handle = s.dataset.saveLater;
    const list = wishGet();
    if (handle && !list.includes(handle)) { list.push(handle); wishSet(list); }
    paintWishHearts();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: wishGet() }));
    changeLine(row.dataset.key, 0).then(() => toast('Moved to your wishlist'));
  });

  paintWishHearts();
  setBadge(H.cartCount || 0);

  /* ---------------- Destructive buttons confirm first ---------------- */
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-confirm]');
    if (!b) return;
    if (!window.confirm(b.dataset.confirm)) e.preventDefault();
  });

  /* ---------------- Cart: discount code ----------------
     `/discount/{code}` is Shopify's own route for attaching a code to the
     session; it then carries through to checkout. The form still submits
     without JS, and Shopify's checkout always has its own code field, so
     nobody is stranded either way. */
  document.addEventListener('submit', e => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    const field = form.querySelector('[name="discount"]');
    const code = (field.value || '').trim();
    const note = $('[data-discount-note]');
    if (!code) {
      e.preventDefault();
      if (note) { note.textContent = 'Enter a code first.'; note.className = 'cart-discount__note bad'; note.hidden = false; }
      field.focus();
      return;
    }
    e.preventDefault();
    /* Shopify strips the query string from the `redirect` target, so the code
       cannot ride back in the URL. Park it in sessionStorage instead — the
       redirect is silent whether or not the code was real, and a shopper who
       gets no answer reads that as a broken button. */
    try { sessionStorage.setItem('hyraluxe:dc', code); } catch (err) { /* private mode */ }
    const back = window.location.pathname;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
  });

  /* ---------------- Cart: take an applied code back off ----------------
     Shopify binds a discount to the cart token itself, not just to the
     `discount_code` cookie, so neither deleting that cookie nor emptying the
     bag shakes it off — the next line item picks the discount straight back
     up, which reads to a shopper as the code applying itself automatically.

     A fresh cart token is the only thing that clears it, so the bag is
     rebuilt: read the lines, drop the token, put the lines back. */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;

    try {
      const state = await (await fetch((routes.cart || '/cart') + '.js')).json();
      const items = (state.items || []).map(li => {
        const item = { id: li.variant_id, quantity: li.quantity };
        if (li.properties && Object.keys(li.properties).length) item.properties = li.properties;
        if (li.selling_plan_allocation) item.selling_plan = li.selling_plan_allocation.selling_plan.id;
        return item;
      });

      ['cart', 'cart_sig', 'cart_ts', 'discount_code'].forEach(name => {
        document.cookie = name + '=; Max-Age=0; path=/';
        document.cookie = name + '=; Max-Age=0; path=/; domain=' + window.location.hostname;
      });

      /* Restore line by line, never in one bulk call. A bulk add is refused
         when any single line outruns its stock — and by this point the old
         cart token is already gone, so one refusal would strand the shopper
         with an emptier bag than they started with. Each line is tried on its
         own, then the rebuilt bag is compared against the snapshot, so what
         gets reported is what actually came back rather than what we hoped. */
      for (const item of items) {
        try {
          await fetch(routes.cartAdd || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [item] })
          });
        } catch (err) { /* measured below, not guessed at here */ }
      }

      const rebuilt = await (await fetch((routes.cart || '/cart') + '.js')).json();
      const want = items.reduce((n, i) => n + i.quantity, 0);
      const got = (rebuilt.items || []).reduce((n, i) => n + i.quantity, 0);

      await refreshCart({ open: true });
      toast(got < want
        ? 'Discount removed. Some pieces had sold out, so your bag was updated.'
        : 'Discount removed.');
    } catch (err) {
      toast('Could not remove the discount. Please try again.');
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------------- Hero: copy the campaign code ----------------
     The chip has carried a `data-copy-code` attribute and a tick since it was
     built, but nothing ever listened for the click: it looked applied and did
     nothing. It copies now, and only then shows the tick. */
  document.addEventListener('click', async e => {
    const chip = e.target.closest('[data-copy-code]');
    if (!chip) return;
    const code = chip.getAttribute('data-copy-code');
    if (!code) return;
    e.preventDefault();

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (err) {
      /* Clipboard is blocked outside a secure context and in some in-app
         browsers. Fall back to a selection the shopper can copy by hand. */
      const tmp = document.createElement('textarea');
      tmp.value = code;
      tmp.setAttribute('readonly', '');
      tmp.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(tmp);
      tmp.select();
      try { copied = document.execCommand('copy'); } catch (err2) { copied = false; }
      document.body.removeChild(tmp);
    }

    if (!copied) { toast('Code: ' + code); return; }

    chip.classList.add('is-copied');
    toast(code + ' copied. Apply it in your bag.');
    window.clearTimeout(chip._copyTimer);
    chip._copyTimer = window.setTimeout(() => chip.classList.remove('is-copied'), 2000);
  });

  /* Report the outcome of a discount attempt once we land back. */
  (async () => {
    let tried = null;
    try {
      tried = sessionStorage.getItem('hyraluxe:dc');
      sessionStorage.removeItem('hyraluxe:dc');
    } catch (err) { /* private mode */ }
    if (!tried) return;

    let applied = false;
    let state = null;
    try {
      state = await (await fetch((routes.cart || '/cart') + '.js')).json();
      const cartLevel = (state.cart_level_discount_applications || []).length > 0;
      const lineLevel = (state.items || []).some(i => (i.discounts || []).length > 0);
      applied = cartLevel || lineLevel;
    } catch (err) { /* leave applied false */ }

    const code = tried.toUpperCase();
    const note = $('[data-discount-note]');
    if (note) {
      note.hidden = false;
      if (applied) {
        note.textContent = code + ' applied.';
        note.className = 'cart-discount__note';
      } else {
        const subtotal = state ? +(state.items_subtotal_price || state.original_total_price || state.total_price || 0) : 0;
        const rakhiMinimum = (+H.freeShip || 999) * 100;
        if (code === 'RAKHI20' && subtotal > 0 && subtotal < rakhiMinimum) {
          note.textContent = code + ' applies on orders above ' + money(rakhiMinimum) + '. Add ' + money(rakhiMinimum - subtotal) + ' more to use it.';
        } else {
          note.textContent = code + ' could not be applied. Check the code, or it may not cover what is in your bag.';
        }
        note.className = 'cart-discount__note bad';
      }
    }
    const drawer = $('#cart-drawer');
    if (drawer && H.page !== 'cart') openEl(drawer);
    if (applied) toast(code + ' applied.');
  })();

  /* ---------------- Header: condense on scroll ---------------- */
  const header = $('.site-header');
  if (header) {
    let stuck = false;
    const onScroll = () => {
      const should = window.scrollY > 40;
      if (should === stuck) return;
      stuck = should;
      header.classList.toggle('is-stuck', should);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------- Header search overlay ----------------
     The icon stays a real link to the search page, so this is an
     enhancement rather than a dependency: with no JS the link works. */
  const ov = $('#search-overlay');
  if (ov) {
    const input = $('#ov-input');
    const results = $('#ov-results');
    const idle = $('#ov-idle');
    const empty = $('#ov-empty');
    const predictive = (routes.predictiveSearch || '/search/suggest');
    let ovOpener = null;
    let timer, lastTerm = '';

    const esc = v => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const CHEV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';

    const rowHTML = p => {
      const img = (p.featured_image && p.featured_image.url) ? p.featured_image.url : (p.image || '');
      const now = toCents(p.price);
      const was = toCents(p.compare_at_price);
      let price = '<span class="price-now">' + money(now) + '</span>';
      if (was > now) {
        const off = Math.round((was - now) * 100 / was);
        price += '<span class="price-mrp">' + money(was) + '</span>' +
                 '<span class="price-off">' + off + '% OFF</span>';
      }
      return '<a class="prow" href="' + esc(p.url) + '">' +
        '<span class="prow__img">' + (img ? '<img src="' + esc(img) + '" alt="" width="64" height="80" loading="lazy">' : '') + '</span>' +
        '<span class="prow__body"><span class="prow__title">' + esc(p.title) + '</span>' +
        '<span class="prow__price">' + price + '</span></span>' + CHEV + '</a>';
    };

    const run = async term => {
      term = (term || '').trim();
      if (term.length < 2) {
        results.hidden = true; empty.hidden = true; idle.hidden = false;
        lastTerm = '';
        return;
      }
      if (term === lastTerm) return;
      lastTerm = term;
      try {
        const url = predictive + '.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        idle.hidden = true;
        results.innerHTML = products.map(rowHTML).join('');
        results.hidden = products.length === 0;
        empty.hidden = products.length > 0;
      } catch (err) { /* the form still submits */ }
    };

    const openOv = () => {
      ovOpener = document.activeElement;
      ov.hidden = false;
      document.body.classList.add('lock');
      setTimeout(() => input.focus(), 40);
    };
    const closeOv = () => {
      ov.hidden = true;
      document.body.classList.remove('lock');
      if (ovOpener && document.contains(ovOpener)) ovOpener.focus();
      ovOpener = null;
    };

    document.addEventListener('click', e => {
      const t = e.target.closest('[data-open-search]');
      if (t) { e.preventDefault(); openOv(); return; }
      if (e.target.closest('[data-search-close]')) { closeOv(); return; }
      if (!ov.hidden && e.target === ov) closeOv();
      const chip = e.target.closest('[data-ov-fill]');
      if (chip) { input.value = chip.dataset.ovFill; input.focus(); run(input.value); }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !ov.hidden) { e.preventDefault(); closeOv(); }
    });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => run(input.value), 220);
    });
  }

  /* ---------------- Reveal on scroll ----------------
     Blocks fade and rise as they arrive. The class is added by script, so a
     page whose JS never runs simply shows everything — the content is never
     hidden by CSS alone. Anyone who has asked for reduced motion is left out
     of it entirely, and the observer stops watching each element once it has
     appeared. */
  const reveal = () => {
    if (!('IntersectionObserver' in window)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (document.body.classList.contains('template-index') === false && H.page !== 'home') return;

    const targets = $$('main.page > .shopify-section');
    if (!targets.length) return;

    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(en => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        obs.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    targets.forEach((el, i) => {
      /* the hero is already on screen; animating it would delay the first paint */
      if (i === 0) return;
      el.classList.add('js-reveal');
      io.observe(el);
    });
  };
  reveal();

  /* ---------------- Product card: colour preview ----------------
     Hovering a colour dot shows that colour in the card's own frame, so the
     shopper can read the range without opening six product pages. The dots
     stay real links, so a tap on a phone goes straight to that variant and
     none of this is needed for the card to work. */
  const cardPreview = (card, src) => {
    const img = card.querySelector('.pcard__img:not(.pcard__img--alt)');
    if (!img) return;
    if (src) {
      if (img.src === src) return;
      img.removeAttribute('srcset');
      img.src = src;
      card.setAttribute('data-preview', '');
    } else {
      const base = img.dataset.baseSrc;
      if (base && img.src !== base) img.src = base;
      card.removeAttribute('data-preview');
    }
  };

  document.addEventListener('mouseover', e => {
    const dot = e.target.closest('.pcard__dot[data-swatch]');
    if (!dot) return;
    const card = dot.closest('.pcard');
    if (!card) return;
    card.querySelectorAll('.pcard__dot.is-live').forEach(d => d.classList.remove('is-live'));
    dot.classList.add('is-live');
    cardPreview(card, dot.dataset.swatch);
  });

  document.addEventListener('focusin', e => {
    const dot = e.target.closest('.pcard__dot[data-swatch]');
    if (!dot) return;
    const card = dot.closest('.pcard');
    if (!card) return;
    card.querySelectorAll('.pcard__dot.is-live').forEach(d => d.classList.remove('is-live'));
    dot.classList.add('is-live');
    cardPreview(card, dot.dataset.swatch);
  });

  document.addEventListener('mouseout', e => {
    const card = e.target.closest('.pcard');
    if (!card) return;
    /* Only when the pointer has actually left the card, not on every hop
       between the dots inside it. */
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    card.querySelectorAll('.pcard__dot.is-live').forEach(d => d.classList.remove('is-live'));
    cardPreview(card, null);
  });

  document.addEventListener('focusout', e => {
    const card = e.target.closest('.pcard');
    if (!card) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    card.querySelectorAll('.pcard__dot.is-live').forEach(d => d.classList.remove('is-live'));
    cardPreview(card, null);
  });

  /* ---------------- Collection filters on desktop ----------------
     Above 900px the filter form is rendered as a permanent sidebar rather
     than a drawer. CSS can make it visible but cannot change attributes, and
     aria-hidden="true" on something a sighted user can read and operate is
     exactly the kind of thing that locks a screen-reader user out of it. */
  const filterForm = document.getElementById('filter-drawer');
  if (filterForm) {
    const asSidebar = window.matchMedia('(min-width: 900px)');
    const syncFilterRole = () => {
      if (asSidebar.matches) {
        filterForm.removeAttribute('aria-hidden');
        filterForm.removeAttribute('aria-modal');
        filterForm.removeAttribute('role');
      } else if (!filterForm.classList.contains('open')) {
        filterForm.setAttribute('aria-hidden', 'true');
        filterForm.setAttribute('aria-modal', 'true');
        filterForm.setAttribute('role', 'dialog');
      }
    };
    syncFilterRole();
    asSidebar.addEventListener('change', syncFilterRole);
  }

  /* ---------------- Recently viewed ---------------- */
  const RV_KEY = 'hyraluxe:recent';
  window.hyraRecent = {
    get() { try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (err) { return []; } },
    push(handle) {
      if (!handle) return;
      const list = this.get().filter(x => x !== handle);
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 8)));
    }
  };
})();
